<template>
  <div
    class="users-activity-log-container users-activity-log-main users-activity-log-page--glass glass-tab-page"
  >
    <BrucdcumHeader :breadcrumb="breadcrumb" />

    <div class="glass-tab-view glass-tab-view--flush users-activity-log-glass-shell">
      <div class="glass-tab-view__content users-activity-log-tab-content users-activity-log-page-stack">
        <template v-if="!accessRestricted">
          <div class="users-activity-log-header-block">
            <div class="users-activity-log-header-row">
              <div class="users-activity-log-header-row__text">
                <h2>Users Activity Log</h2>
                <p class="users-activity-log-header-row__sub">
                  Review client and invited user actions across your company
                </p>
              </div>
              <div class="users-activity-log-header-actions">
                <div v-if="selectedCount > 0" class="users-activity-log-selection-bar">
                  <span class="users-activity-log-selection-badge">
                    <i class="bi bi-check2-square" aria-hidden="true"></i>
                    <span><strong>{{ selectedCount }}</strong> selected</span>
                  </span>
                  <SecondaryButton
                    label="Clear"
                    icon="bi bi-x-lg"
                    @click="clearSelection"
                  />
                </div>
                <SecondaryButton
                  v-if="selectedCount > 0"
                  :label="deleting ? 'Deleting…' : `Delete Logs (${selectedCount})`"
                  :icon="deleting ? undefined : 'bi bi-trash'"
                  custom-class="consultant-secondary-btn--danger"
                  :loading="deleting"
                  :disabled="deleting"
                  @click="openDeleteConfirm"
                />
                <SecondaryButton
                  v-else
                  label="Export to CSV"
                  icon="bi bi-download"
                  @click="exportCSV"
                />
              </div>
            </div>
          </div>

          <div class="users-activity-log-filters users-activity-log-filters-toolbar-row">
            <div class="users-activity-log-filters-left">
              <LabelDropdown
                label="Date range"
                select-id="ual-range"
                v-model="dateRange"
                inline
                :searchable="false"
                :options="dateRangeOptions"
                @change="onDateRangeChange"
              />
              <template v-if="isCustomRange">
                <div class="users-activity-log-filter-field users-activity-log-filter-field--date">
                  <LabelDateField
                    input-id="ual-from-date"
                    label="From date"
                    v-model="fromDate"
                    placeholder="MM/DD/YYYY"
                    @change="onCustomDateChange"
                  />
                </div>
                <div class="users-activity-log-filter-field users-activity-log-filter-field--date">
                  <LabelDateField
                    input-id="ual-end-date"
                    label="End date"
                    v-model="endDate"
                    placeholder="MM/DD/YYYY"
                    @change="onCustomDateChange"
                  />
                </div>
              </template>
              <LabelDropdown
                label="User type"
                select-id="ual-actor"
                v-model="actorFilter"
                inline
                :searchable="false"
                :options="actorFilterOptions"
                @change="onActorFilterChange"
              />
              <LabelDropdown
                label="Module"
                select-id="ual-module"
                v-model="moduleFilter"
                inline
                :searchable="true"
                :options="moduleFilterOptions"
                @change="onModuleFilterChange"
              />
              <LabelDropdown
                label="Actor"
                select-id="ual-actor-name"
                v-model="actorNameFilter"
                inline
                searchable
                search-placeholder="Search actors..."
                :options="actorNameFilterOptions"
                @change="onActorNameFilterChange"
              />
            </div>
          </div>

          <div class="users-activity-log-legend" aria-label="Actor type legend">
            <span class="users-activity-log-legend-item">
              <span
                class="users-activity-log-legend-swatch users-activity-log-legend-swatch--client"
                aria-hidden="true"
              ></span>
              <span class="users-activity-log-actor--client">Client (owner)</span>
            </span>
            <span class="users-activity-log-legend-item">
              <span
                class="users-activity-log-legend-swatch users-activity-log-legend-swatch--invited"
                aria-hidden="true"
              ></span>
              <span class="users-activity-log-actor--invited">Invited user</span>
            </span>
          </div>

          <div class="users-activity-log-glass-panel users-activity-log-main-card">
            <div class="consultant-glass-table-section users-activity-log-table-section">
              <div
                class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
                :class="{ 'consultant-glass-table-responsive--loading': loading }"
              >
                <div class="consultant-glass-table-scroll-x">
                  <table class="consultant-glass-table users-activity-log-table">
                    <thead>
                      <tr>
                        <th class="users-activity-log-col-select">
                          <label
                            class="users-activity-log-checkbox"
                            :title="allOnPageSelected ? 'Deselect all on page' : 'Select all on page'"
                          >
                            <input
                              type="checkbox"
                              :checked="allOnPageSelected"
                              :indeterminate.prop="someOnPageSelected && !allOnPageSelected"
                              :disabled="paginatedLogs.length === 0"
                              @change="toggleSelectAllOnPage($event.target.checked)"
                            />
                            <span class="users-activity-log-checkbox-sr">Select all on this page</span>
                          </label>
                        </th>
                        <th>Logged At</th>
                        <th>Actor</th>
                        <th>Company</th>
                        <th>Action</th>
                        <th>Module</th>
                        <th>Record</th>
                        <th>Details</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loading">
                        <td colspan="9" class="consultant-glass-table-no-records users-activity-log-no-records">
                          Loading activity logs...
                        </td>
                      </tr>
                      <tr v-else-if="paginatedLogs.length === 0">
                        <td colspan="9" class="consultant-glass-table-no-records users-activity-log-no-records">
                          No activity logs found for selected filters
                        </td>
                      </tr>
                      <template v-else>
                        <tr
                          v-for="(row, idx) in paginatedLogs"
                          :key="row.id"
                          class="consultant-glass-table-row users-activity-log-row"
                          :class="{ 'users-activity-log-row--selected': isSelected(row.id) }"
                          :style="{ '--ual-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                        >
                          <td class="users-activity-log-col-select">
                            <label class="users-activity-log-checkbox">
                              <input
                                type="checkbox"
                                :checked="isSelected(row.id)"
                                @change="toggleRowSelection(row.id, $event.target.checked)"
                              />
                              <span class="users-activity-log-checkbox-sr">Select log {{ row.id }}</span>
                            </label>
                          </td>
                          <td class="users-activity-log-col-logged">{{ row.loggedAt || '—' }}</td>
                          <td>
                            <span :class="actorClass(row)">{{ row.actorName || row.actorEmail || '—' }}</span>
                          </td>
                          <td>{{ row.companyName || companyName || '—' }}</td>
                          <td>{{ row.action || '—' }}</td>
                          <td>{{ row.module || '—' }}</td>
                          <td>{{ row.recordName || row.recordType || '—' }}</td>
                          <td class="users-activity-log-col-details">{{ formatDetails(row) }}</td>
                          <td>{{ row.status || 'Success' }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div class="users-activity-log-pagination-wrap">
              <PaginationComponent
                :current-page="currentPage"
                :total-items="filteredLogs.length"
                :items-per-page="itemsPerPage"
                item-label="records"
                :show-items-per-page="true"
                @page-change="onPageChange"
                @items-per-page-change="onItemsPerPageChange"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <ErrorModal
      :show="showAccessModal"
      title="Access restricted"
      :message="accessModalMessage"
      button-text="OK"
      @close="closeAccessModal"
    />

    <WarningModal
      :show="showDeleteConfirm"
      title="Delete activity logs?"
      :message="deleteConfirmMessage"
      button-text="Cancel"
      confirm-button-text="Delete"
      @close="closeDeleteConfirm"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import '../styles/UsersActivityLog.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import axios from 'axios';
import logger from '@/utils/logger';

export default {
  name: 'UsersActivityLog',
  components: {
    BrucdcumHeader,
    PaginationComponent,
    ErrorModal,
    WarningModal,
    LabelDropdown,
    LabelDateField,
    SecondaryButton,
  },
  data() {
    const today = new Date();
    const todayUsa = formatUsaDate(today);
    return {
      dateRange: 'today',
      fromDate: todayUsa,
      endDate: todayUsa,
      actorFilter: 'all',
      actorNameFilter: 'all',
      moduleFilter: 'all',
      activityLogs: [],
      companyName: '',
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
      accessRestricted: false,
      showAccessModal: false,
      accessModalMessage: '',
      selectedIds: [],
      showDeleteConfirm: false,
      deleting: false,
    };
  },
  computed: {
    isCustomRange() {
      return this.dateRange === 'custom';
    },
    dateRangeOptions() {
      return [
        { value: 'today', label: 'Today' },
        { value: 'this_week', label: 'This week' },
        { value: 'this_month', label: 'This month' },
        { value: 'custom', label: 'Custom' },
      ];
    },
    actorFilterOptions() {
      return [
        { value: 'all', label: 'All users' },
        { value: 'client', label: 'Client (owner)' },
        { value: 'invited', label: 'Invited users' },
      ];
    },
    moduleFilterOptions() {
      return [
        { value: 'all', label: 'All modules' },
        { value: 'Authentication', label: 'Authentication' },
        { value: 'Users & Permissions', label: 'Users & Permissions' },
        { value: 'Company', label: 'Company' },
        { value: 'Settings', label: 'Settings' },
        { value: 'Customer', label: 'Customer' },
        { value: 'Vendor', label: 'Vendor' },
        { value: 'Consultant', label: 'Consultant' },
        { value: 'Journal Entry', label: 'Journal Entry' },
        { value: 'Inventory', label: 'Inventory' },
        { value: 'Warehouse', label: 'Warehouse' },
        { value: 'Banking', label: 'Banking' },
        { value: 'Reports', label: 'Reports' },
        { value: 'Client', label: 'Client' },
        { value: 'General', label: 'General' },
      ];
    },
    actorNameFilterOptions() {
      return [
        { value: 'all', label: 'All actors' },
        ...this.actorOptions.map((opt) => ({ value: opt.value, label: opt.label })),
      ];
    },
    actorOptions() {
      const map = new Map();
      (this.activityLogs || []).forEach((row) => {
        const name = (row.actorName || '').trim();
        const email = (row.actorEmail || '').trim();
        if (!name && !email) return;
        const value = (email || name).toLowerCase();
        if (map.has(value)) return;
        const label = name ? (email ? `${name} (${email})` : name) : email;
        map.set(value, { value, label });
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    },
    filteredLogs() {
      const withoutPresence = (this.activityLogs || []).filter((row) => !this.isPresenceOnlineOfflineLog(row));
      let rows = withoutPresence;
      if (this.moduleFilter && this.moduleFilter !== 'all') {
        const target = String(this.moduleFilter).toLowerCase();
        rows = rows.filter((row) => String(row.module || '').trim().toLowerCase() === target);
      }
      if (!this.actorNameFilter || this.actorNameFilter === 'all') {
        return rows;
      }
      const target = String(this.actorNameFilter).toLowerCase();
      return rows.filter((row) => {
        const email = (row.actorEmail || '').trim().toLowerCase();
        const name = (row.actorName || '').trim().toLowerCase();
        return email === target || name === target;
      });
    },
    paginatedLogs() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredLogs.slice(start, start + this.itemsPerPage);
    },
    breadcrumb() {
      const company = this.companyName ? this.companyName : 'Company';
      return [company, 'Users Activity Log'];
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    allOnPageSelected() {
      return (
        this.paginatedLogs.length > 0 &&
        this.paginatedLogs.every((row) => this.selectedSet.has(row.id))
      );
    },
    someOnPageSelected() {
      return this.paginatedLogs.some((row) => this.selectedSet.has(row.id));
    },
    deleteConfirmMessage() {
      const n = this.selectedCount;
      const word = n === 1 ? 'record' : 'records';
      return `You are about to permanently delete ${n} activity log ${word}. This action cannot be undone.`;
    },
  },
  mounted() {
    this.applyDateRange();
  },
  methods: {
    formatUsaDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${m}/${day}/${y}`;
    },
    toIsoDate(usaDate) {
      if (!usaDate) return '';
      const parts = usaDate.split('/');
      if (parts.length !== 3) return '';
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },
    onDateRangeChange() {
      this.applyDateRange();
    },
    onCustomDateChange() {
      if (!this.isCustomRange) return;
      this.currentPage = 1;
      this.fetchActivityLogs();
    },
    onActorFilterChange() {
      this.actorNameFilter = 'all';
      this.currentPage = 1;
      this.fetchActivityLogs();
    },
    onModuleFilterChange() {
      this.currentPage = 1;
      this.fetchActivityLogs();
    },
    onActorNameFilterChange() {
      this.currentPage = 1;
      const validIds = new Set(this.filteredLogs.map((r) => r.id));
      this.selectedIds = this.selectedIds.filter((id) => validIds.has(id));
    },
    actorClass(row) {
      if (!row) return '';
      if (row.actorIsInvited === true) return 'users-activity-log-actor--invited';
      if (row.actorIsInvited === false) return 'users-activity-log-actor--client';
      return '';
    },
    isPresenceOnlineOfflineLog(row) {
      const action = String(row?.action || '').trim().toLowerCase();
      return action === 'user online' || action === 'user offline';
    },
    applyDateRange() {
      const today = new Date();

      if (this.dateRange === 'today') {
        const t = this.formatUsaDate(today);
        this.fromDate = t;
        this.endDate = t;
      } else if (this.dateRange === 'this_week') {
        const first = new Date(today);
        const day = first.getDay();
        const diffToMonday = day === 0 ? 6 : day - 1;
        first.setDate(first.getDate() - diffToMonday);
        this.fromDate = this.formatUsaDate(first);
        this.endDate = this.formatUsaDate(today);
      } else if (this.dateRange === 'this_month') {
        const first = new Date(today.getFullYear(), today.getMonth(), 1);
        this.fromDate = this.formatUsaDate(first);
        this.endDate = this.formatUsaDate(today);
      }

      this.currentPage = 1;
      this.fetchActivityLogs();
    },
    formatLoggedAt(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    },
    rewriteDetailsToDeviceTime(text) {
      if (!text || typeof text !== 'string') return text;
      let out = text;
      out = out.replace(/<<TS:([^>]+)>>/g, (_, iso) => this.formatLoggedAt(iso));
      out = out.replace(
        /(\d{2}\/\d{2}\/\d{4}),?\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)\s+UTC/gi,
        (full, mdy, hh, mm, ss, ap) => {
          const [m, d, y] = mdy.split('/').map((n) => parseInt(n, 10));
          let hour = parseInt(hh, 10) % 12;
          if (/pm/i.test(ap)) hour += 12;
          const utcDate = new Date(
            Date.UTC(y, m - 1, d, hour, parseInt(mm, 10), parseInt(ss, 10))
          );
          if (Number.isNaN(utcDate.getTime())) return full;
          return this.formatLoggedAt(utcDate.toISOString());
        }
      );
      return out;
    },
    formatLogRow(log) {
      const changesText =
        Array.isArray(log.changes) && log.changes.length
          ? log.changes.map((c) => `${c.field}: ${c.from ?? '—'} -> ${c.to ?? '—'}`).join(' | ')
          : '';
      const rawTs = log.loggedAt || log.createdAt || log.updatedAt;
      return {
        id: log.id,
        loggedAt: this.formatLoggedAt(rawTs),
        loggedAtIso: rawTs,
        actorName: log.actorName || null,
        actorEmail: log.actorEmail || null,
        actorIsInvited: typeof log.actorIsInvited === 'boolean' ? log.actorIsInvited : null,
        action: log.action || 'Updated',
        module: log.module || 'General',
        recordType: log.recordType || null,
        recordName: log.recordName || null,
        recordId: log.recordId || null,
        companyId: log.companyId || null,
        companyName: log.companyName || this.companyName || null,
        details: log.details || '—',
        changesText,
        status: log.status || 'Success',
      };
    },
    formatDetails(row) {
      if (!row) return '—';
      let base = row.details && row.details !== '—' ? row.details : '';
      base = this.rewriteDetailsToDeviceTime(base);
      if (row.changesText) {
        base = `${base}${base ? ' | ' : ''}${row.changesText}`;
      }
      return base || '—';
    },
    async fetchActivityLogs() {
      const companyId = this.$route.params.id;
      if (!companyId) return;

      this.loading = true;
      try {
        const { data } = await axios.get(`/company/${companyId}/activity-logs`, {
          params: {
            fromDate: this.toIsoDate(this.fromDate),
            endDate: this.toIsoDate(this.endDate),
            actor: this.actorFilter || 'all',
            ...(this.moduleFilter && this.moduleFilter !== 'all'
              ? { module: this.moduleFilter }
              : {}),
          },
        });
        if (!data?.success) {
          this.activityLogs = [];
          this.selectedIds = [];
          return;
        }
        this.accessRestricted = false;
        this.companyName = data?.company?.name || this.companyName || '';
        const rows = Array.isArray(data.logs) ? data.logs : [];
        this.activityLogs = rows.map((log) => this.formatLogRow(log));
        const validIds = new Set(this.activityLogs.map((r) => r.id));
        this.selectedIds = this.selectedIds.filter((id) => validIds.has(id));
      } catch (e) {
        logger.error('Failed to fetch activity logs:', e);
        const msg = e.response?.data?.message;
        if (msg) {
          this.accessModalMessage = msg;
          this.accessRestricted = true;
          this.showAccessModal = true;
        }
        this.activityLogs = [];
      } finally {
        this.loading = false;
      }
    },
    closeAccessModal() {
      this.showAccessModal = false;
    },
    onPageChange(page) {
      this.currentPage = page;
    },
    onItemsPerPageChange(size) {
      this.itemsPerPage = size;
      this.currentPage = 1;
    },
    isSelected(id) {
      return this.selectedSet.has(id);
    },
    toggleRowSelection(id, checked) {
      if (checked) {
        if (!this.selectedSet.has(id)) this.selectedIds = [...this.selectedIds, id];
      } else {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },
    toggleSelectAllOnPage(checked) {
      const pageIds = this.paginatedLogs.map((row) => row.id);
      if (checked) {
        const merged = new Set([...this.selectedIds, ...pageIds]);
        this.selectedIds = Array.from(merged);
      } else {
        const pageSet = new Set(pageIds);
        this.selectedIds = this.selectedIds.filter((x) => !pageSet.has(x));
      }
    },
    clearSelection() {
      this.selectedIds = [];
    },
    exportCSV() {
      const source =
        this.selectedIds.length > 0
          ? this.activityLogs.filter((row) => this.selectedSet.has(row.id))
          : this.filteredLogs;
      if (source.length === 0) {
        if (this.$toast) this.$toast.info('No logs to export');
        return;
      }
      const headers = [
        'Logged At',
        'Actor',
        'Company',
        'Action',
        'Module',
        'Record',
        'Details',
        'Status',
      ];
      const escape = (val) => {
        const s = val == null ? '' : String(val);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = source.map((row) => [
        row.loggedAt || '',
        row.actorName || row.actorEmail || '',
        row.companyName || this.companyName || '',
        row.action || '',
        row.module || '',
        row.recordName || row.recordType || '',
        this.formatDetails(row).replace(/\s+/g, ' ').trim(),
        row.status || 'Success',
      ]);
      const csv = [headers.map(escape).join(','), ...rows.map((r) => r.map(escape).join(','))].join(
        '\n'
      );
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const tag = this.selectedIds.length > 0 ? 'selected' : 'all';
      a.href = url;
      a.download = `users-activity-log-${tag}-${this.toIsoDate(this.fromDate) || 'today'}-to-${this.toIsoDate(this.endDate) || 'today'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    openDeleteConfirm() {
      if (!this.selectedCount) return;
      this.showDeleteConfirm = true;
    },
    closeDeleteConfirm() {
      if (this.deleting) return;
      this.showDeleteConfirm = false;
    },
    async confirmDelete() {
      const companyId = this.$route.params.id;
      const ids = [...this.selectedIds];
      if (!companyId || ids.length === 0) {
        this.showDeleteConfirm = false;
        return;
      }
      this.deleting = true;
      try {
        const { data } = await axios.delete(`/company/${companyId}/activity-logs`, {
          data: { ids },
        });
        if (!data?.success) {
          if (this.$toast) this.$toast.error(data?.message || 'Failed to delete logs');
          return;
        }
        if (this.$toast) {
          this.$toast.success(`Deleted ${data.deletedCount ?? ids.length} log(s)`);
        }
        this.selectedIds = [];
        this.showDeleteConfirm = false;
        await this.fetchActivityLogs();
      } catch (e) {
        logger.error('Failed to delete activity logs:', e);
        const msg = e.response?.data?.message || 'Failed to delete logs';
        if (this.$toast) this.$toast.error(msg);
        else {
          this.accessModalMessage = msg;
          this.showAccessModal = true;
        }
      } finally {
        this.deleting = false;
      }
    },
  },
};

function formatUsaDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${m}/${day}/${y}`;
}
</script>
